import styled from '@emotion/styled'

import theme from './theme'
import { rem, media } from './tools'

export const headlineStyles = {
  fontWeight: 800,
  fontSize: rem(33),
  [media.mobileLg]: { fontSize: rem(40) },
  [media.laptop]: { fontSize: '4.05vmax' },
  lineHeight: 1.1,
}

export const Headline = styled.p(headlineStyles)

export const ColorHeadline = styled.p(
  headlineStyles,
  {
    display: 'inline',
    fontSize: rem(26),
    span: {
      position: 'relative',
      zIndex: 0,
      '::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        bottom: '10%',
        left: '0.05em',
        right: '0.05em',
        height: '17%',
        transition: '.3s',
      },
    },
    ':hover span::after': {
      height: '40%',
    },
  },
  ({ color, underlineColors }) => ({
    'span::after': {
      backgroundImage:
        color ||
        (underlineColors &&
          `linear-gradient(to right, ${color || underlineColors[0]}, ${color ||
            underlineColors[1]})`),
    },
  })
)

const headingBaseStyles = {
  fontWeight: 800,
  lineHeight: 1.1,
}

export const boldText = {
  fontWeight: 700,
  fontSize: rem(18),
  [media.laptop]: { fontSize: '1.45vw' },
  lineHeight: 1.6,
}

export const BoldText = styled.p(boldText)

export const paragraphStyles = {
  fontWeight: 400,
  fontSize: rem(16),
  lineHeight: 1.6,
  fontFamily: 'SourceSerifPro, serif',
  [media.laptop]: {
    fontSize: rem(18),
    lineHeight: 1.9,
  },
}

export const Paragraph = styled.p(paragraphStyles)

export const wideTextStyles = {
  fontWeight: 400,
  fontSize: rem(16),
  lineHeight: 1,
  letterSpacing: '0.2em',
  fontFamily: 'TungstenMedium, sans-serif',
  textTransform: 'uppercase',
}

export const dynamicUnderlineStyles = {
  display: 'inline-block',
  position: 'relative',

  '&::after': {
    content: '""',
    display: 'block',
    width: 'calc(100% - 0.15em)',
    height: 2,
    position: 'absolute',
    top: '0.73em',
    left: 0,
    backgroundColor: 'currentColor',
    transform: 'scaleX(0)',
    transformOrigin: 'center right',
    transition: 'transform 0.5s cubic-bezier(0.55, 0, 0.1, 1)',
  },

  '.hoverWideText:hover &::after, &.active::after, &:hover::after, &:focus::after': {
    transform: 'scaleX(1)',
    transformOrigin: 'center left',
  },
}

export const WideText = styled.span(wideTextStyles, dynamicUnderlineStyles)

export const underlineStyles = {
  display: 'inline',
  backgroundRepeat: 'no-repeat',
  boxDecorationBreak: 'clone',
  transitionProperty: 'opacity, background',
  transitionDuration: '.2s',
  transitionTimingFunction: 'ease-out',
  backgroundImage: 'linear-gradient(to right, #e0e0e0, #e0e0e0)',
  backgroundPosition: '0.07em 0.85em',
  backgroundSize: 'calc(100% - 0.1em) 0.5em',
  ':hover': {
    backgroundPosition: '0.07em 0em',
    backgroundSize: 'calc(100% - 0.1em) 1.35em',
  },
}

export const blockquoteParagraph = {
  position: 'relative',
  marginBottom: rem(15),
  paddingLeft: '2em',
  fontWeight: 400,
  fontSize: rem(18),
  lineHeight: 1.6,
  fontFamily: 'SourceSerifPro, serif',
  color: theme.colors.grey_900,
  [media.laptop]: {
    fontSize: rem(24),
  },

  '&::before, &::after': {
    fontWeight: 800,
    fontFamily: 'RidleyGrotesk, sans-serif',
  },

  '&::before': {
    content: '"“"',
    position: 'absolute',
    top: '-0.34em',
    left: 0,
    fontSize: '275%',
  },

  '&::after': {
    content: '"”"',
  },
}

export const blockquoteAuthor = {
  fontWeight: 800,
  fontSize: rem(16),
  fontFamily: 'RidleyGrotesk, sans-serif',
  color: theme.colors.grey_900,
  textAlign: 'right',
  [media.laptop]: {
    fontSize: rem(18),
  },
}

export const h2Styles = {
  fontSize: rem(24),
  lineHeight: 1.4,
}

export const H2 = styled.h2(headingBaseStyles, h2Styles)

export const postStyles = {
  'div, h1, h2, h3, h4, h5, h6, hgroup, ul, ol, dl, dd, p, figure, pre, table, fieldset, blockquote, form, noscript, iframe, img, hr, address': {
    marginBottom: rem(30),
  },
  'h1, h2, h3, h4, h5, h6': {
    ...headingBaseStyles,
    '* + &': {
      marginTop: rem(60),
    },
  },
  'ul, ol': {
    marginTop: rem(30),
    marginBottom: rem(30),
  },
  'img, .gatsby-resp-image-link': {
    marginTop: rem(50),
    marginBottom: rem(50),
  },
  '*:first-child, *:first-child > *': {
    marginTop: 0,
  },
  h1: { fontSize: rem(24) },
  h2: h2Styles,
  'h3, h4, h5, h6': { fontSize: rem(18) },
  'p, li': paragraphStyles,
  'a:not(.custom-link)': {
    fontWeight: 700,
    ...underlineStyles,
  },
  '.tweet-link': {
    transition: '.2s',
    ':hover': {
      color: '#fff',
      backgroundColor: '#55acee',
      '::after': {
        background: `url('data:image/svg+xml;utf8,<svg%20viewBox%3D"0%200%2024%2024"%20xmlns%3D"http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg"%20fill%3D"%23fff"><path%20d%3D"M22.46%2C6C21.69%2C6.35%2020.86%2C6.58%2020%2C6.69C20.88%2C6.16%2021.56%2C5.32%2021.88%2C4.31C21.05%2C4.81%2020.13%2C5.16%2019.16%2C5.36C18.37%2C4.5%2017.26%2C4%2016%2C4C13.65%2C4%2011.73%2C5.92%2011.73%2C8.29C11.73%2C8.63%2011.77%2C8.96%2011.84%2C9.27C8.28%2C9.09%205.11%2C7.38%203%2C4.79C2.63%2C5.42%202.42%2C6.16%202.42%2C6.94C2.42%2C8.43%203.17%2C9.75%204.33%2C10.5C3.62%2C10.5%202.96%2C10.3%202.38%2C10C2.38%2C10%202.38%2C10%202.38%2C10.03C2.38%2C12.11%203.86%2C13.85%205.82%2C14.24C5.46%2C14.34%205.08%2C14.39%204.69%2C14.39C4.42%2C14.39%204.15%2C14.36%203.89%2C14.31C4.43%2C16%206%2C17.26%207.89%2C17.29C6.43%2C18.45%204.58%2C19.13%202.56%2C19.13C2.22%2C19.13%201.88%2C19.11%201.54%2C19.07C3.44%2C20.29%205.7%2C21%208.12%2C21C16%2C21%2020.33%2C14.46%2020.33%2C8.79C20.33%2C8.6%2020.33%2C8.42%2020.32%2C8.23C21.16%2C7.63%2021.88%2C6.87%2022.46%2C6Z"%2F><%2Fsvg>')`,
      },
    },
    '::after': {
      content: "''",
      display: 'inline-block',
      width: '1.3em',
      height: '1.3em',
      color: '#55acee',
      background: `url('data:image/svg+xml;utf8,<svg%20viewBox%3D"0%200%2024%2024"%20xmlns%3D"http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg"%20fill%3D"%2355acee"><path%20d%3D"M22.46%2C6C21.69%2C6.35%2020.86%2C6.58%2020%2C6.69C20.88%2C6.16%2021.56%2C5.32%2021.88%2C4.31C21.05%2C4.81%2020.13%2C5.16%2019.16%2C5.36C18.37%2C4.5%2017.26%2C4%2016%2C4C13.65%2C4%2011.73%2C5.92%2011.73%2C8.29C11.73%2C8.63%2011.77%2C8.96%2011.84%2C9.27C8.28%2C9.09%205.11%2C7.38%203%2C4.79C2.63%2C5.42%202.42%2C6.16%202.42%2C6.94C2.42%2C8.43%203.17%2C9.75%204.33%2C10.5C3.62%2C10.5%202.96%2C10.3%202.38%2C10C2.38%2C10%202.38%2C10%202.38%2C10.03C2.38%2C12.11%203.86%2C13.85%205.82%2C14.24C5.46%2C14.34%205.08%2C14.39%204.69%2C14.39C4.42%2C14.39%204.15%2C14.36%203.89%2C14.31C4.43%2C16%206%2C17.26%207.89%2C17.29C6.43%2C18.45%204.58%2C19.13%202.56%2C19.13C2.22%2C19.13%201.88%2C19.11%201.54%2C19.07C3.44%2C20.29%205.7%2C21%208.12%2C21C16%2C21%2020.33%2C14.46%2020.33%2C8.79C20.33%2C8.6%2020.33%2C8.42%2020.32%2C8.23C21.16%2C7.63%2021.88%2C6.87%2022.46%2C6Z"%2F><%2Fsvg>')`,
    },
  },
  blockquote: {
    '> p': blockquoteParagraph,
    '> footer': blockquoteAuthor,
  },
  // Ensure li > p is 1/2 margin — this is another markdown => compiler oddity.
  // 'li, li > p': {
  //   marginBottom: rem(5),
  // },
  'ul li': {
    listStyle: 'inside disc',
  },
  'ol li': {
    listStyle: 'inside decimal-leading-zero',
  },
}

export const Detail = styled.span(({ block, muted }) => ({
  display: block ? 'block' : 'inline-block',
  letterSpacing: '0.2em',
  fontWeight: 'normal',
  fontSize: 15,
  fontFamily: 'TungstenMedium, sans-serif',
  textTransform: 'uppercase',
  WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)',
  opacity: muted && 0.15,
}))

export const DetailValue = styled.span({
  fontWeight: 700,
  fontSize: 15,
  lineHeight: 1.5,
})

export const Strikethrough = styled.s(({ inView, length, delay = 0 }) => ({
  display: 'inline',
  textDecoration: 'none',
  backgroundImage: 'linear-gradient(currentColor, currentColor)',
  backgroundPositionY: '62%',
  backgroundPositionX: inView ? 0 : `${length}em`,
  backgroundSize: '100% 20%',
  backgroundRepeat: 'no-repeat',
  transition: '1s ease-out',
  transitionDelay: `${delay}ms`,
  ':hover': {
    opacity: 0.6,
    backgroundPositionX: `${length}em !important`,
    transition: '1s ease-out',
  },
}))
